/**
 * Product page campaign helpers.
 *
 * <s-countdown data-end="UNIX_SECONDS"> updates its [data-cd="d|h|m|s"] children every
 * second and hides itself when the end time passes. Server-rendered values show first.
 *
 * <s-copy-code data-code="CODE" data-copied="Copied"> copies CODE to the clipboard when
 * its button is pressed and briefly swaps [data-copy-label] to the copied text.
 */
const pad = (n) => String(n).padStart(2, '0');

class Countdown extends HTMLElement {
  connectedCallback() {
    this.end = Number(this.dataset.end) * 1000;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
    const values = {
      d: Math.floor(left / 86400),
      h: Math.floor((left % 86400) / 3600),
      m: Math.floor((left % 3600) / 60),
      s: left % 60,
    };
    for (const el of this.querySelectorAll('[data-cd]')) el.textContent = pad(values[el.dataset.cd]);
    if (left === 0) {
      clearInterval(this.timer);
      const wrapper = this.parentElement;
      if (wrapper) wrapper.hidden = true;
    }
  }
}

class CopyCode extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('[data-copy-label]');
    this.onClick = this.onClick.bind(this);
    if (this.button) this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  async onClick() {
    const code = this.dataset.code || '';
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    if (!this.label) return;
    this.label.textContent = this.dataset.copied || code;
    clearTimeout(this.reset);
    this.reset = setTimeout(() => {
      this.label.textContent = code;
    }, 1800);
  }
}

if (!customElements.get('s-countdown')) customElements.define('s-countdown', Countdown);
if (!customElements.get('s-copy-code')) customElements.define('s-copy-code', CopyCode);

/**
 * <s-read-more data-more="..." data-less="..."> clamps [data-read-more-body] and shows
 * [data-read-more-toggle] only when the content is taller than the clamp.
 */
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-read-more-body]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    this.fade = this.querySelector('[data-read-more-fade]');
    if (!this.body || !this.toggle) return;
    this.onToggle = this.onToggle.bind(this);
    this.toggle.addEventListener('click', this.onToggle);
    requestAnimationFrame(() => {
      const clamped = this.body.scrollHeight > this.body.clientHeight + 4;
      this.toggle.hidden = !clamped;
      if (this.fade) this.fade.hidden = !clamped;
    });
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
  }

  onToggle() {
    const open = !this.body.hasAttribute('data-expanded');
    this.body.toggleAttribute('data-expanded', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.dataset.less : this.dataset.more;
    if (this.fade) this.fade.hidden = open;
  }
}

if (!customElements.get('s-read-more')) customElements.define('s-read-more', ReadMore);

/**
 * <s-buy-now data-form="FORM_ID"> wraps a link to a cart permalink (/cart/VARIANT:QTY).
 * On click it rebuilds the link from the form's current variant id and quantity, so
 * the buyer goes straight to checkout with what they selected. Without JS the link
 * still opens checkout with the variant rendered by the server and quantity 1.
 */
class BuyNow extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    if (!this.link) return;
    this.onClick = this.onClick.bind(this);
    this.link.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = document.getElementById(this.dataset.form || '');
    const id = form?.querySelector('input[name="id"]')?.value;
    if (!id) return;
    const qtyInput = form.querySelector('input[name="quantity"]');
    const qty = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);
    event.preventDefault();
    window.location.href = `${window.Shopify?.routes?.root || '/'}cart/${id}:${qty}`;
  }
}

if (!customElements.get('s-buy-now')) customElements.define('s-buy-now', BuyNow);

/**
 * <s-rail> drives a horizontal product rail. Its track ([data-rail-track]) may be
 * inserted later (product recommendations load async), so it is looked up on demand.
 * [data-rail-prev] / [data-rail-next] scroll by one visible page; [data-rail-progress]
 * shows how far the rail is scrolled. Buttons disable at the ends.
 */
class Rail extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.update = this.update.bind(this);
    this.addEventListener('click', this.onClick);
    this.observer = new MutationObserver(() => this.bind());
    this.observer.observe(this, { childList: true, subtree: true });
    this.bind();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
    this.track?.removeEventListener('scroll', this.update);
  }

  bind() {
    const track = this.querySelector('[data-rail-track]');
    if (!track || track === this.track) return;
    this.track?.removeEventListener('scroll', this.update);
    this.track = track;
    track.addEventListener('scroll', this.update, { passive: true });
    this.update();
  }

  onClick(event) {
    const button = event.target.closest('[data-rail-prev], [data-rail-next]');
    if (!button || !this.track) return;
    const dir = button.hasAttribute('data-rail-next') ? 1 : -1;
    const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: dir * rtl * this.track.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' });
  }

  update() {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    const prev = this.querySelector('[data-rail-prev]');
    const next = this.querySelector('[data-rail-next]');
    if (prev) prev.disabled = pos <= 2;
    if (next) next.disabled = pos >= max - 2;
    const bar = this.querySelector('[data-rail-progress]');
    if (bar) {
      const visible = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      const ratio = max > 0 ? pos / max : 0;
      bar.style.width = `${Math.max(visible, 0.08) * 100}%`;
      bar.style.insetInlineStart = `${ratio * (1 - visible) * 100}%`;
      bar.parentElement.hidden = max <= 2;
    }
    this.toggleAttribute('data-scrollable', max > 2);
  }
}

if (!customElements.get('s-rail')) customElements.define('s-rail', Rail);
